import React, { useEffect, useRef } from 'react';
import classnames from 'classnames';
import { FormLabel } from '../FormLabel/FormLabel';
import RedactedInput from '../RedactedInput/RedactedInput';
import FormError from '../FormError/FormError';

export type ToggleCheckboxProps = {
	disabled?: boolean;
	onClick: (value: boolean) => void;
	value: boolean;
	label?: string;
	extraClasses?: string;
	required?: boolean;
	tooltip?: string;
	name: string;
	inline?: boolean;
	grey?: boolean;
	redacted?: boolean;
	tooltipPosition?: 'top' | 'right' | 'bottom' | 'left';
	error?: string;
	id?: string;
	small?: boolean;
};

export function ToggleCheckbox({
	onClick,
	disabled = false,
	label,
	value,
	required = false,
	extraClasses,
	tooltip,
	name,
	grey = false,
	inline = false,
	redacted = false,
	tooltipPosition = 'left',
	error,
	small = false,
	id,
}: ToggleCheckboxProps): JSX.Element {
	const ref = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (ref?.current?.disabled && disabled === true) {
			ref.current.disabled = disabled;
		}
	}, [disabled]);

	if (redacted) {
		return (
			<RedactedInput
				name={name}
				required={required}
				label={label}
				tooltip={tooltip}
				white={grey === false}
			/>
		);
	}

	return (
		<div
			className={classnames(
				extraClasses,
				'flex text-base font-semibold w-full',
				inline
					? 'flex-row justify-between items-center'
					: 'flex-col justify-start',
				{ 'pointer-events-none opacity-70': disabled },
			)}
		>
			<FormLabel
				name={name}
				label={label}
				tooltip={tooltip}
				tooltipPosition={tooltipPosition}
				required={required}
				absolute={false}
			/>
			<div
				className={classnames(
					'relative inline-block align-middle select-none transition duration-200 ease-in',
					{ 'w-12': small, 'w-16': small === false },
				)}
				id={id}
			>
				<input
					type='checkbox'
					checked={value}
					className={classnames('toggle-checkbox', small && 'small')}
					aria-label={label ?? 'toggle checkbox'}
					onChange={event => onClick(event.target.checked)}
					name={name}
					ref={ref}
				/>
				<label
					htmlFor='toggle'
					className={classnames(
						'toggle-label transition-all duration-300 block overflow-hidden rounded-full cursor-pointer',
						grey ? 'bg-grey-300' : 'bg-pink-900',
						small ? 'h-6' : 'h-7',
					)}
					onClick={() => onClick(!value)}
				/>
			</div>
			{error && <FormError error={error} />}
		</div>
	);
}
